import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { WalletContext } from '../../context/WalletContext';
import { ARBITRUM_CHAIN_ID, ARBITRUM_SEPOLIA_CHAIN_ID } from './constants';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

function drawEquity(canvas, closes) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.offsetWidth, H = canvas.offsetHeight;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const PAD = { t: 10, r: 8, b: 24, l: 52 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;

  ctx.clearRect(0, 0, W, H);

  // Build cumulative curve (oldest → newest), starting from 0
  const sorted = [...closes].reverse();
  const curve = [0];
  let cum = 0;
  for (const t of sorted) { cum += t.pnl; curve.push(cum); }

  const minV = Math.min(0, ...curve);
  const maxV = Math.max(0, ...curve);
  const range = maxV - minV || 1;

  const toX = i => PAD.l + (i / Math.max(curve.length - 1, 1)) * cW;
  const toY = v => PAD.t + cH - ((v - minV) / range) * cH;

  // ── Grid lines ──
  ctx.strokeStyle = '#1a1a1a';
  ctx.lineWidth   = 0.5;
  for (let g = 0; g <= 4; g++) {
    const y = PAD.t + (g / 4) * cH;
    ctx.beginPath(); ctx.moveTo(PAD.l, y); ctx.lineTo(PAD.l + cW, y); ctx.stroke();
  }
  for (let g = 0; g <= 4; g++) {
    const x = PAD.l + (g / 4) * cW;
    ctx.beginPath(); ctx.moveTo(x, PAD.t); ctx.lineTo(x, PAD.t + cH); ctx.stroke();
  }

  // ── $0 baseline ──
  const zeroY = toY(0);
  ctx.strokeStyle = '#2a2a2a';
  ctx.lineWidth   = 1;
  ctx.setLineDash([3, 4]);
  ctx.beginPath(); ctx.moveTo(PAD.l, zeroY); ctx.lineTo(PAD.l + cW, zeroY); ctx.stroke();
  ctx.setLineDash([]);

  if (curve.length < 2) return; // only the 0-origin point, no trades yet

  const finalVal = curve[curve.length - 1];
  const lineColor = finalVal >= 0 ? '#26c49a' : '#c0392b';
  const fillColor = finalVal >= 0 ? 'rgba(38,196,154,0.10)' : 'rgba(192,57,43,0.10)';

  // ── Area fill ──
  ctx.beginPath();
  ctx.moveTo(toX(0), zeroY);
  curve.forEach((v, i) => ctx.lineTo(toX(i), toY(v)));
  ctx.lineTo(toX(curve.length - 1), zeroY);
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();

  // ── Equity line ──
  ctx.beginPath();
  curve.forEach((v, i) => i === 0 ? ctx.moveTo(toX(i), toY(v)) : ctx.lineTo(toX(i), toY(v)));
  ctx.strokeStyle = lineColor;
  ctx.lineWidth   = 1.5;
  ctx.stroke();

  // ── Y-axis labels ──
  ctx.fillStyle  = '#444'; ctx.font = '8px monospace'; ctx.textAlign = 'right';
  const fmt = v => {
    const abs = Math.abs(v);
    const s = abs >= 1000 ? `${(abs/1000).toFixed(1)}K` : abs >= 1 ? abs.toFixed(0) : abs.toFixed(2);
    return (v >= 0 ? '+' : '-') + '$' + s;
  };
  [[0, zeroY], [maxV, PAD.t + 4], [minV, PAD.t + cH - 2]].forEach(([v, y]) => {
    if (Math.abs(y - zeroY) > 10 || v === 0)
      ctx.fillText(fmt(v), PAD.l - 4, y + 3);
  });

  // ── X-axis trade count labels ──
  ctx.fillStyle = '#333'; ctx.textAlign = 'center';
  const step = Math.max(1, Math.floor(curve.length / 4));
  for (let i = 0; i < curve.length; i += step) {
    ctx.fillText(i + 1, toX(i), PAD.t + cH + 14);
  }
  ctx.fillText(curve.length, toX(curve.length - 1), PAD.t + cH + 14);
}

function SessionRow({ address, countdown }) {
  if (!address && !countdown) return null;
  const short = address ? `${address.slice(0,6)}…${address.slice(-4)}` : '';
  return (
    <div className="pf-session">
      <span className="pf-wallet">{short}</span>
      {countdown && <span className="pf-countdown">⏱ {countdown}</span>}
    </div>
  );
}

function useSessionInfo() {
  const [info, setInfo] = useState({ countdown: '', address: '' });
  useEffect(() => {
    const tick = () => {
      try {
        const token = localStorage.getItem('xyr_dashboard_token');
        if (!token) { setInfo({ countdown: '', address: '' }); return; }
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const addr = payload.address || '';
        const secsLeft = payload.exp - Math.floor(Date.now() / 1000);
        if (secsLeft <= 0) { setInfo({ countdown: 'expired', address: addr }); return; }
        const h = Math.floor(secsLeft / 3600);
        const m = Math.floor((secsLeft % 3600) / 60);
        const s = secsLeft % 60;
        setInfo({
          countdown: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`,
          address: addr,
        });
      } catch { setInfo({ countdown: '', address: '' }); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return info;
}

export default function PortfolioPanel({ venue = 'hyperliquid' }) {
  const { connected, address: walletAddress, chainId } = useContext(WalletContext);
  const [trades,   setTrades]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [balance,  setBalance]  = useState(null);
  const [fetchErr, setFetchErr] = useState(null);
  // true when the backend could only scan a recent slice of history (its wide-range
  // RPC was down). The stats below would then be computed from a partial record, so
  // say it rather than present a truncated equity curve as the whole account.
  const [partial,  setPartial]  = useState(false);
  const canvasRef = useRef(null);
  const { countdown, address: jwtAddress } = useSessionInfo();
  const address = walletAddress || jwtAddress;

  const isTestnet = chainId === ARBITRUM_SEPOLIA_CHAIN_ID;

  // ── Hyperliquid data ──────────────────────────────────────────────────────
  useEffect(() => {
    if (venue !== 'hyperliquid' || !connected || !address) return;

    const HL_URL = 'https://api.hyperliquid.xyz/info';
    const user   = address.toLowerCase();
    let active = true;

    const fetchBalance = () =>
      Promise.all([
        axios.post(HL_URL, { type: 'clearinghouseState',     user }, { timeout: 15000 }),
        axios.post(HL_URL, { type: 'spotClearinghouseState', user }, { timeout: 15000 }),
      ]).then(([perpsRes, spotRes]) => {
        if (!active) return;
        const accountValue = parseFloat(perpsRes.data?.marginSummary?.accountValue || 0);
        const spotBalances = spotRes.data?.balances || [];
        const spotUsdc = spotBalances.find(b => b.coin === 'USDC');
        const spotUsdcBal = parseFloat(spotUsdc?.total || 0);
        setBalance(accountValue + spotUsdcBal);
      }).catch(() => {});

    const doFetch = () => {
      setLoading(true);
      Promise.all([
        axios.post(HL_URL, { type: 'userFills', user }, { timeout: 30000 }),
        fetchBalance(),
      ]).then(([fillsRes]) => {
          if (!active) return;
          const fills = Array.isArray(fillsRes.data) ? fillsRes.data : [];
          const mapped = fills.map(f => ({
            id:      f.tid,
            type:    f.dir.startsWith('Open') ? 'OPEN' : 'CLOSE',
            market:  f.coin + '/USD',
            isLong:  f.dir.includes('Long'),
            sizeUsd: parseFloat(f.sz) * parseFloat(f.px),
            price:   parseFloat(f.px),
            pnl:     f.dir.startsWith('Close') ? parseFloat(f.closedPnl || '0') : null,
            ts:      f.time,
          }));
          mapped.sort((a, b) => b.ts - a.ts);
          setTrades(mapped);
        })
        .catch(() => {})
        .finally(() => { if (active) setLoading(false); });
    };

    setTrades([]); setBalance(null); setFetchErr(null);
    doFetch();
    const t = setInterval(doFetch, 30_000);
    return () => { active = false; clearInterval(t); };
  }, [venue, connected, address]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GMX data (on-chain via backend) ──────────────────────────────────────
  useEffect(() => {
    if (venue !== 'gmx' || !connected || !address) return;

    const chainId = isTestnet ? ARBITRUM_SEPOLIA_CHAIN_ID : ARBITRUM_CHAIN_ID;
    let active = true;

    const doFetch = () => {
      setLoading(true);
      axios.get(`${API_BASE}/proxy/gmx-trades`, {
        params: { account: address.toLowerCase(), chainId },
        timeout: 60000, // on-chain scan can take ~30s
      })
        .then(r => {
          if (!active) return;
          const raw = r.data?.trades || [];
          setTrades(raw);
          setPartial(Boolean(r.data?.partial));
          setBalance(null);
        })
        .catch(() => { if (active) setFetchErr('GMX history unavailable'); })
        .finally(() => { if (active) setLoading(false); });
    };

    setTrades([]); setBalance(null); setFetchErr(null); setPartial(false);
    doFetch();
    // Long interval — on-chain scan is expensive
    const t = setInterval(doFetch, 5 * 60_000);
    return () => { active = false; clearInterval(t); };
  }, [venue, connected, address, isTestnet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closed trades with real PnL, oldest→newest for equity curve
  const closes = trades.filter(t => t.type === 'CLOSE' && t.pnl !== null);

  useEffect(() => {
    if (!canvasRef.current) return;
    drawEquity(canvasRef.current, closes);
  }, [closes]);

  // ── Stats ──
  const wins    = closes.filter(t => t.pnl > 0);
  const losses  = closes.filter(t => t.pnl <= 0);
  const totalPnl    = closes.reduce((s, t) => s + t.pnl, 0);
  const grossProfit = wins.reduce((s, t) => s + t.pnl, 0);
  const grossLoss   = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
  const pf   = grossLoss > 0 ? (grossProfit / grossLoss) : (grossProfit > 0 ? Infinity : 0);
  const avgW = wins.length   ? grossProfit / wins.length   : 0;
  const avgL = losses.length ? grossLoss   / losses.length : 0;
  const best  = closes.length ? Math.max(...closes.map(t => t.pnl)) : 0;
  const worst = closes.length ? Math.min(...closes.map(t => t.pnl)) : 0;
  const winRate = closes.length ? (wins.length / closes.length * 100) : 0;

  const fmtUsd = v => {
    const abs = Math.abs(v);
    const s = abs >= 1000 ? `$${(abs/1000).toFixed(1)}K` : abs >= 1 ? `$${abs.toFixed(0)}` : `$${abs.toFixed(2)}`;
    return (v >= 0 ? '+' : '-') + s;
  };
  const green = '#26c49a', red = '#c0392b', dim = '#444';

  if (!connected) {
    return (
      <div className="portfolio-panel">
        <div className="pf-header">PORTFOLIO</div>
        <SessionRow address={address} countdown={countdown} />
        <div className="pf-empty">connect wallet</div>
      </div>
    );
  }

  const venueLabel = venue === 'hyperliquid' ? 'HYPE' : venue === 'gmx' ? 'GMX' : venue.toUpperCase();

  return (
    <div className="portfolio-panel">
      <div className="pf-header">
        <span>PORTFOLIO · {venueLabel}</span>
        <span className="pf-count">{closes.length} TRADES</span>
      </div>
      <SessionRow address={address} countdown={countdown} />

      {partial && (
        <div className="pf-session" style={{ color: '#c9a227' }}>
          partial history — RPC degraded, stats cover a recent slice only
        </div>
      )}

      {/* Account balance — HL only */}
      {venue === 'hyperliquid' && (
        <div className="pf-balance-row">
          <span className="pf-balance-label">HYPE BAL</span>
          <span className="pf-balance-val">
            {balance === null ? '—' : `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
        </div>
      )}

      {/* Equity curve */}
      <div className="pf-chart-wrap">
        <canvas ref={canvasRef} className="pf-canvas" />
        {loading && closes.length === 0 && (
          <div className="pf-chart-empty">loading…</div>
        )}
        {!loading && fetchErr && (
          <div className="pf-chart-empty" style={{ color: '#c0392b' }}>{fetchErr}</div>
        )}
        {!loading && !fetchErr && closes.length === 0 && (
          <div className="pf-chart-empty">no closed trades</div>
        )}
      </div>

      {/* Stats grid */}
      {closes.length > 0 && (
        <div className="pf-grid">
          <div className="pf-stat">
            <div className="pf-label">WIN RATE</div>
            <div className="pf-val" style={{ color: winRate >= 50 ? green : red }}>
              {winRate.toFixed(0)}%
            </div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">TOTAL PNL</div>
            <div className="pf-val" style={{ color: totalPnl >= 0 ? green : red }}>
              {fmtUsd(totalPnl)}
            </div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">PROFIT FACTOR</div>
            <div className="pf-val" style={{ color: pf >= 1 ? green : red }}>
              {pf === Infinity ? '∞' : pf.toFixed(2)}
            </div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">AVG WIN</div>
            <div className="pf-val" style={{ color: green }}>{fmtUsd(avgW)}</div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">AVG LOSS</div>
            <div className="pf-val" style={{ color: red }}>-${avgL >= 1 ? avgL.toFixed(0) : avgL.toFixed(2)}</div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">BEST TRADE</div>
            <div className="pf-val" style={{ color: green }}>{fmtUsd(best)}</div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">WORST TRADE</div>
            <div className="pf-val" style={{ color: red }}>{fmtUsd(worst)}</div>
          </div>
          <div className="pf-stat">
            <div className="pf-label">W / L</div>
            <div className="pf-val" style={{ color: dim }}>
              <span style={{ color: green }}>{wins.length}</span>
              {' / '}
              <span style={{ color: red }}>{losses.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Trade history */}
      <div className="tc-positions" style={{ marginTop: 8 }}>
        <div className="tc-positions-header">
          <span className="tc-label">HISTORY</span>
        </div>
        <div className="tc-history-scroll">
          {loading && trades.length === 0 ? (
            <div className="tc-positions-empty">loading…</div>
          ) : trades.length === 0 ? (
            <div className="tc-positions-empty">no history</div>
          ) : [...trades].reverse().map(h => {
            const s = Math.floor((Date.now() - h.ts) / 1000);
            const timeAgo = s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s/60)}m` : s < 86400 ? `${Math.floor(s/3600)}h` : `${Math.floor(s/86400)}d`;
            return (
              <div key={h.id} className="tc-hist-row" style={{ opacity: h.type === 'OPEN' ? 0.6 : 1 }}>
                <span className={`tc-position-side ${h.isLong ? 'long' : 'short'}`}>
                  {h.market.replace('/USD','')}·{h.isLong ? 'L' : 'S'}
                </span>
                <span className="tc-hist-type">{h.type}</span>
                <span className="tc-hist-num">${h.sizeUsd > 0 ? (h.sizeUsd >= 1000 ? `${(h.sizeUsd/1000).toFixed(0)}K` : h.sizeUsd.toFixed(0)) : '—'}</span>
                <span className="tc-hist-num">@${h.price > 0 ? h.price.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}</span>
                {h.pnl !== null
                  ? <span className={`tc-position-pnl ${h.pnl >= 0 ? 'pos' : 'neg'}`}>
                      {h.pnl >= 0 ? '+' : ''}{h.pnl.toFixed(2)}
                    </span>
                  : <span className="tc-hist-num">—</span>
                }
                <span className="tc-hist-time">{timeAgo}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
